import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import Divider from '~/bootcamp/Activities/components/Divider'
import Input from '~/bootcamp/Activities/components/Input'
import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { generateDenominatorNumerator } from '~/bootcamp/Activities/Fractions/Fractions.utils'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

const mediaQuery = [{ screen: '(min-width: 280px)', width: '80px' }]

import { generateImageSrcListAndGroupIndex } from './FractionIllustrated.utils'
import {
  MIN_NUMERATORS,
  MAX_NUMERATORS,
  MIN_DENOMINATORS,
  MAX_DENOMINATORS,
  MAX_HISTORY_LENGTH,
  MAX_GROUP_HISTORY_LENGTH,
  GROUPS,
} from './Constants'

function FractionIllustrated({
  answerState,
  difficultyLevel,
  onNextQuestionClick,
  onAnswerStateChange,
  onInstanceChange,
  autofocus = true,
  actionButtonsFC: SubmitButton,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation(['bootcamp', 'generics'])

  const [answer, setAnswer] = useState({ denominator: '', numerator: '' })
  const [numeratorHistory, setNumeratorHistory] = useState<number[]>([])
  const [groupHistory, setGroupHistory] = useState<number[]>([])
  const [imageData, setImageData] = useState({ imageSrcList: [''], groupIndex: 0, selectedItemIndex: 0 })
  const [{ denominator, numerator }, setNumbers] = useState({
    denominator: 1,
    numerator: 1,
  })
  const [inputKey, setInputKey] = useState<string>(uuid())

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  useEffect(() => {
    const generatedNumbers = generateDenominatorNumerator(
      MIN_NUMERATORS[difficultyLevel - 1],
      MAX_NUMERATORS[difficultyLevel - 1],
      MIN_DENOMINATORS[difficultyLevel - 1],
      MAX_DENOMINATORS[difficultyLevel - 1],
      numeratorHistory,
    )
    setNumbers(generatedNumbers)
    setImageData(
      generateImageSrcListAndGroupIndex(generatedNumbers.denominator, generatedNumbers.numerator, groupHistory),
    )
    setAnswer({ denominator: '', numerator: '' })
  }, [numeratorHistory, difficultyLevel, groupHistory])

  // The instruction asks for both halves at every level, so the task is that text plus two empty boxes
  // and the whole fraction rides the guarded channel.
  const instruction = t('bootcamp:fractions.illustrated.how_many', {
    group: t(GROUPS[imageData.groupIndex].nameKey),
    item: t(GROUPS[imageData.groupIndex].items[imageData.selectedItemIndex].nameKey),
  })
  const paintedTask = `${instruction}; \\frac{\\square}{\\square}`
  const guardedValue = `\\frac{${numerator}}{${denominator}}`
  useEmitInstance(onInstanceChange, denominator > 1 ? paintedTask : null, denominator > 1 ? guardedValue : null)

  useEffect(() => {
    onAnswerStateChange(null)
  }, [onAnswerStateChange])

  const handleOnCheckAnswerClick = (): void => {
    const isCorrect = answer.denominator === denominator.toString() && answer.numerator === numerator.toString()
    onAnswerStateChange(isCorrect)
  }

  const resetAnswer = (): void => {
    setAnswer({ denominator: '', numerator: '' })
  }

  const handleOnNextQuestionClick = (): void => {
    setNumeratorHistory((prevState) => {
      return [numerator, ...prevState].slice(0, MAX_HISTORY_LENGTH[difficultyLevel - 1])
    })
    setGroupHistory((prevState) => {
      return [imageData.groupIndex, ...prevState].slice(0, MAX_GROUP_HISTORY_LENGTH - 1)
    })
    onAnswerStateChange(null)
    onNextQuestionClick()
    setInputKey(uuid())
  }
  const onEnterPress = (): void => {
    if (answer.denominator !== '' && answer.numerator !== '') {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper className="flex-col">
        <div className='text-black font-normal text-md mt-4"'>{instruction}</div>
        <div className="flex flex-row flex-wrap justify-center mb-5 mt-5">
          {imageData.imageSrcList.map((imageSrcKey, index) => (
            <ResponsiveImage
              height={80}
              imageWidths="80"
              key={imageSrcKey + index.toString()}
              mediaQuery={mediaQuery}
              srcKey={imageSrcKey}
              width={80}
            />
          ))}
        </div>
        <div className="ml-4 flex flex-col items-center">
          <Input
            aria-label={t('generics:ariaLabelButtons.insertNumerator')}
            autoFocus={autofocus}
            isError={answerState === false}
            key={`${inputKey}-first`}
            maxDigits={4}
            onChange={(value) => setAnswer((state) => ({ denominator: state.denominator, numerator: value }))}
            onEnter={onEnterPress}
            ref={inputRef}
            value={answer.numerator}
          />
          <Divider className="my-2" />
          <Input
            aria-label={t('generics:ariaLabelButtons.insertDenominator')}
            isError={answerState === false}
            key={`${inputKey}-second`}
            maxDigits={4}
            onChange={(value) => setAnswer((state) => ({ denominator: value, numerator: state.numerator }))}
            onEnter={onEnterPress}
            value={answer.denominator}
          />
        </div>
      </FractionsActivityWrapper>
      {SubmitButton && (
        <SubmitButton
          clearFormAction={() => resetAnswer()}
          correctAnswer={answerState ?? false}
          formValid={answer.denominator !== '' && answer.numerator !== ''}
          goToNextQuestionAction={handleOnNextQuestionClick}
          submitAnswerAction={handleOnCheckAnswerClick}
        />
      )}
    </ActivityWrapper>
  )
}

export default FractionIllustrated
